import { CheckCircle2, Scale } from "lucide-react";
import { fmtEUR } from "../lib/calculations";
import type { ComputedResults } from "../lib/types";

interface Props {
  results: ComputedResults;
}

export function RegimeComparator({ results }: Props) {
  const reelMieux = results.impotsReel <= results.impotsMicroBIC;
  const ecart = Math.abs(results.impotsReel - results.impotsMicroBIC);
  const baseImposableMicro = results.revenusBrutsAnnuels * 0.5;

  return (
    <section className="mx-auto max-w-6xl px-6 py-8 lg:px-12">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h3 className="flex items-center gap-2 text-lg font-bold text-foreground"><Scale className="h-5 w-5 text-primary" aria-hidden />Régime réel vs micro-BIC</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Comparaison fiscale basée sur une TMI de 30% + prélèvements sociaux 17,2%.
        </p>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2"></th>
                <th className="py-2 font-medium">Régime réel</th>
                <th className="py-2 font-medium">Micro-BIC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <Row label="Revenus locatifs">
                <span className="tabular">{fmtEUR(results.revenusBrutsAnnuels)}</span>
                <span className="tabular">{fmtEUR(results.revenusBrutsAnnuels)}</span>
              </Row>
              <Row label="Charges déductibles">
                <span>Réelles ({fmtEUR(results.chargesDeductibles)})</span>
                <span>50% forfait ({fmtEUR(baseImposableMicro)})</span>
              </Row>
              <Row label="Amortissements">
                <span className="tabular">{fmtEUR(results.amortissementAnnuel)}</span>
                <span className="text-muted-foreground">Non applicables</span>
              </Row>
              <Row label="Impôts estimés">
                <span className="tabular font-semibold">{fmtEUR(results.impotsReel)}</span>
                <span className="tabular font-semibold">{fmtEUR(results.impotsMicroBIC)}</span>
              </Row>
              <Row label="Résultat net annuel" highlight>
                <span className="tabular font-bold text-foreground">
                  {fmtEUR(results.resultatNetReel, { sign: true })}
                </span>
                <span className="tabular font-bold text-foreground">
                  {fmtEUR(results.resultatNetMicroBIC, { sign: true })}
                </span>
              </Row>
            </tbody>
          </table>
        </div>

        <div
          className={`mt-5 rounded-xl border p-4 ${
            reelMieux
              ? "border-success/30 bg-success/5"
              : "border-primary/30 bg-primary/5"
          }`}
        >
          <p className="flex items-start gap-2 text-sm text-foreground">
            <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${reelMieux ? "text-success" : "text-primary"}`} aria-hidden />
            <span>
              {reelMieux ? (
                <>
                  Dans votre cas, le <strong>régime réel est plus avantageux</strong> :
                  économie de <strong className="tabular">{fmtEUR(ecart)}/an</strong>
                </>
              ) : (
                <>
                  Dans votre cas, le <strong>micro-BIC suffit</strong> (simplicité +
                  fiscalité similaire, écart de {fmtEUR(ecart)}/an)
                </>
              )}
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}

function Row({
  label,
  children,
  highlight,
}: {
  label: string;
  children: [React.ReactNode, React.ReactNode];
  highlight?: boolean;
}) {
  return (
    <tr className={highlight ? "bg-secondary/40" : ""}>
      <td className="py-2.5 text-muted-foreground">{label}</td>
      <td className="py-2.5 text-foreground">{children[0]}</td>
      <td className="py-2.5 text-foreground">{children[1]}</td>
    </tr>
  );
}
